import React, { useState, useRef, useCallback, useEffect } from 'react'
import { Camera, Upload, Search, QrCode, X, AlertCircle } from 'lucide-react'

import Button from '../ui/Button'
import Input from '../ui/Input'
import { useToast } from '../Toast'

import { Produto } from '../../types'
import produtosService from '../../services/produtos'

interface QRCodeScannerProps {
  onSuccess: (produto: Produto) => void
  onCancel: () => void
}

const QRCodeScanner: React.FC<QRCodeScannerProps> = ({
  onSuccess,
  onCancel
}) => {
  const { success, error } = useToast()
  
  // Estados locais
  const [isScanning, setIsScanning] = useState(false)
  const [manualCode, setManualCode] = useState('')
  const [isSearching, setIsSearching] = useState(false)
  const [cameraError, setCameraError] = useState<string | null>(null)
  const [hasPermission, setHasPermission] = useState<boolean | null>(null)
  const [isDetecting, setIsDetecting] = useState(false)
  
  // Refs
  const videoRef = useRef<HTMLVideoElement>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const scanIntervalRef = useRef<NodeJS.Timeout | null>(null)

  // Verificar permissões de câmera
  const checkCameraPermissions = async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setCameraError('Câmera não suportada neste navegador')
        setHasPermission(false)
        return false
      }

      // Verificar se já tem permissão
      if (navigator.permissions) {
        const permission = await navigator.permissions.query({ name: 'camera' as PermissionName })
        if (permission.state === 'denied') {
          setCameraError('Permissão de câmera negada. Por favor, habilite nas configurações do navegador.')
          setHasPermission(false)
          return false
        }
      }

      setHasPermission(true)
      setCameraError(null)
      return true
    } catch (err) {
      console.warn('Erro ao verificar permissões:', err)
      setHasPermission(null)
      return null
    }
  }

  // Handlers
  const handleStartCamera = async () => {
    const hasPerms = await checkCameraPermissions()
    if (hasPerms === false) return

    try {
      // Tentar diferentes configurações de câmera
      const constraints = [
        // Primeira tentativa: câmera traseira com resolução alta
        {
          video: {
            facingMode: 'environment',
            width: { ideal: 1280 },
            height: { ideal: 720 }
          }
        },
        // Segunda tentativa: qualquer câmera com resolução menor
        {
          video: {
            width: { ideal: 640 },
            height: { ideal: 480 }
          }
        },
        // Terceira tentativa: configuração mínima
        {
          video: true
        }
      ]

      let stream: MediaStream | null = null
      let lastError: Error | null = null

      for (const constraint of constraints) {
        try {
          stream = await navigator.mediaDevices.getUserMedia(constraint)
          break
        } catch (err) {
          lastError = err as Error
          console.warn('Tentativa de câmera falhou:', constraint, err)
        }
      }

      if (!stream) {
        throw lastError || new Error('Não foi possível acessar a câmera')
      }
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream
        streamRef.current = stream
        setIsScanning(true)
        setCameraError(null)
        setHasPermission(true)
        
        // Aguardar o vídeo carregar antes de iniciar detecção
        videoRef.current.onloadedmetadata = () => {
          startQRDetection()
        }
      }
    } catch (err: any) {
      console.error('Erro ao acessar câmera:', err)
      let errorMessage = 'Erro ao acessar câmera'
      let errorDetail = 'Verifique as permissões de câmera do navegador'
      
      if (err.name === 'NotAllowedError') {
        errorMessage = 'Permissão negada'
        errorDetail = 'Por favor, permita o acesso à câmera e recarregue a página'
        setHasPermission(false)
      } else if (err.name === 'NotFoundError') {
        errorMessage = 'Câmera não encontrada'
        errorDetail = 'Nenhuma câmera foi detectada no dispositivo'
      } else if (err.name === 'NotSupportedError') {
        errorMessage = 'Câmera não suportada'
        errorDetail = 'Este navegador não suporta acesso à câmera'
      }
      
      setCameraError(`${errorMessage}: ${errorDetail}`)
      error(errorMessage, errorDetail)
    }
  }

  const handleStopCamera = () => {
    // Parar detecção
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current)
      scanIntervalRef.current = null
    }
    setIsDetecting(false)
    
    // Parar stream da câmera
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop())
      streamRef.current = null
    }
    setIsScanning(false)
    setCameraError(null)
  }

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    // Verificar se é imagem
    if (!file.type.startsWith('image/')) {
      error('Arquivo inválido', 'Por favor, selecione uma imagem')
      return
    }

    // Processar imagem para detecção de QR code
    const reader = new FileReader()
    reader.onload = async (e) => {
      try {
        const imageDataUrl = e.target?.result as string
        await processImageForQR(imageDataUrl)
      } catch (err) {
        error('Erro ao processar imagem', 'Não foi possível detectar QR code na imagem')
      }
    }
    reader.readAsDataURL(file)
  }

  // Processar imagem para detecção de QR code
  const processImageForQR = async (imageDataUrl: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const img = new Image()
      img.onload = () => {
        try {
          if (!canvasRef.current) {
            reject(new Error('Canvas não disponível'))
            return
          }

          const canvas = canvasRef.current
          const ctx = canvas.getContext('2d')
          if (!ctx) {
            reject(new Error('Contexto canvas não disponível'))
            return
          }

          canvas.width = img.width
          canvas.height = img.height
          ctx.drawImage(img, 0, 0)

          // Tentar detectar QR code na imagem
          detectQRFromCanvas(canvas).then(resolve).catch(reject)
        } catch (err) {
          reject(err)
        }
      }
      img.onerror = () => reject(new Error('Erro ao carregar imagem'))
      img.src = imageDataUrl
    })
  }

  const handleManualSearch = async () => {
    if (!manualCode.trim()) {
      error('Código obrigatório', 'Digite o SKU ou código de barras para buscar')
      return
    }

    setIsSearching(true)
    try {
      const produto = await produtosService.buscarPorCodigo(manualCode.trim())
      success('Produto encontrado!')
      onSuccess(produto)
    } catch (err: any) {
      if (err.message.includes('404')) {
        error('Produto não encontrado', 'Nenhum produto encontrado com este código')
      } else {
        error('Erro na busca', err.message)
      }
    } finally {
      setIsSearching(false)
    }
  }

  const handleScanResult = useCallback(async (code: string) => {
    try {
      // Tentar primeiro como JSON (QR code gerado pelo sistema)
      const qrData = JSON.parse(code)
      if (qrData.type === 'produto' && qrData.id) {
        const produto = await produtosService.getById(qrData.id)
        success('Produto encontrado via QR Code!')
        onSuccess(produto)
        return
      }
    } catch {
      // Se não for JSON, tratar como código de barras/SKU
      try {
        const produto = await produtosService.buscarPorCodigo(code)
        success('Produto encontrado!')
        onSuccess(produto)
      } catch (err: any) {
        error('Produto não encontrado', 'Nenhum produto encontrado com este código')
      }
    }
  }, [onSuccess, success, error])

  // Detecção de QR code (implementação simples)
  const startQRDetection = () => {
    if (!videoRef.current || !canvasRef.current) return

    setIsDetecting(true)
    
    scanIntervalRef.current = setInterval(async () => {
      try {
        await captureAndAnalyzeFrame()
      } catch (err) {
        console.warn('Erro na detecção:', err)
      }
    }, 500) // Escanear a cada 500ms
  }

  const captureAndAnalyzeFrame = async () => {
    if (!videoRef.current || !canvasRef.current) return

    const video = videoRef.current
    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')
    
    if (!ctx || video.readyState !== 4) return

    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    ctx.drawImage(video, 0, 0)

    await detectQRFromCanvas(canvas)
  }

  const detectQRFromCanvas = async (canvas: HTMLCanvasElement): Promise<void> => {
    // Implementação básica de detecção de código de barras/QR code
    // Em um projeto real, seria usado uma biblioteca como jsQR ou quagga.js
    
    // Simular detecção para códigos de teste
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    // Analisar a imagem (implementação simplificada)
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
    const result = await simulateQRDetection(imageData)
    
    if (result) {
      handleStopCamera()
      await handleScanResult(result)
    }
  }

  // Simulação de detecção de QR code (para demonstração)
  const simulateQRDetection = async (imageData: ImageData): Promise<string | null> => {
    // Esta é uma implementação simulada
    // Em um projeto real, seria usado jsQR ou similar:
    // import jsQR from 'jsqr'
    // const code = jsQR(imageData.data, imageData.width, imageData.height)
    // return code ? code.data : null
    
    return null // Por enquanto não detecta nada automaticamente
  }

  // Verificar permissões na inicialização
  useEffect(() => {
    checkCameraPermissions()
  }, [])

  // Cleanup ao desmontar
  useEffect(() => {
    return () => {
      handleStopCamera()
    }
  }, [])

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <QrCode className="h-12 w-12 text-blue-600 mx-auto mb-2" />
        <h3 className="text-lg font-medium text-gray-900">Scanner QR Code</h3>
        <p className="text-gray-600">
          Escaneie um QR code, código de barras ou digite manualmente
        </p>
      </div>

      {/* Opções de busca */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Button
          variant="outline"
          onClick={isScanning ? handleStopCamera : handleStartCamera}
          leftIcon={isScanning ? <X className="h-4 w-4" /> : <Camera className="h-4 w-4" />}
          fullWidth
        >
          {isScanning ? 'Parar Câmera' : 'Usar Câmera'}
        </Button>

        <Button
          variant="outline"
          onClick={() => fileInputRef.current?.click()}
          leftIcon={<Upload className="h-4 w-4" />}
          fullWidth
        >
          Upload Imagem
        </Button>

        <Button
          variant="outline"
          onClick={handleManualSearch}
          leftIcon={<Search className="h-4 w-4" />}
          isLoading={isSearching}
          fullWidth
        >
          Busca Manual
        </Button>
      </div>

      {/* Mensagem de erro de câmera */}
      {cameraError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start space-x-2">
            <AlertCircle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="text-sm font-medium text-red-800">Erro de Câmera</h4>
              <p className="text-sm text-red-700 mt-1">{cameraError}</p>
              {hasPermission === false && (
                <div className="mt-2">
                  <p className="text-xs text-red-600">
                    Para usar a câmera:
                  </p>
                  <ul className="text-xs text-red-600 mt-1 ml-4">
                    <li>• Clique no ícone de câmera na barra de endereços</li>
                    <li>• Selecione "Permitir" para acesso à câmera</li>
                    <li>• Recarregue a página</li>
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Área da câmera */}
      {isScanning && (
        <div className="relative bg-black rounded-lg overflow-hidden">
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="w-full h-64 object-cover"
          />
          
          {/* Overlay de foco */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="relative w-48 h-48 border-2 border-white rounded-lg opacity-75">
              <div className="absolute top-0 left-0 w-6 h-6 border-t-4 border-l-4 border-blue-500"></div>
              <div className="absolute top-0 right-0 w-6 h-6 border-t-4 border-r-4 border-blue-500"></div>
              <div className="absolute bottom-0 left-0 w-6 h-6 border-b-4 border-l-4 border-blue-500"></div>
              <div className="absolute bottom-0 right-0 w-6 h-6 border-b-4 border-r-4 border-blue-500"></div>
              
              {/* Indicador de detecção */}
              {isDetecting && (
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                  <div className="animate-pulse">
                    <QrCode className="h-8 w-8 text-blue-400" />
                  </div>
                </div>
              )}
            </div>
          </div>
          
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-white text-sm bg-black bg-opacity-50 px-3 py-1 rounded">
            {isDetecting ? 'A detectar códigos...' : 'Posicione o código dentro do quadrado'}
          </div>
        </div>
      )}

      {/* Canvas oculto para processamento */}
      <canvas ref={canvasRef} className="hidden" />

      {/* Input manual */}
      <div className="space-y-3">
        <Input
          label="Busca Manual"
          placeholder="Digite o SKU ou código de barras"
          value={manualCode}
          onChange={(e) => setManualCode(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              handleManualSearch()
            }
          }}
          rightElement={
            <Button
              size="sm"
              onClick={handleManualSearch}
              isLoading={isSearching}
              disabled={!manualCode.trim()}
            >
              <Search className="h-4 w-4" />
            </Button>
          }
        />
      </div>

      {/* Input de arquivo escondido */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileUpload}
        className="hidden"
      />

      {/* Instruções */}
      <div className="bg-blue-50 p-4 rounded-lg">
        <h4 className="font-medium text-blue-900 mb-2">Como usar:</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• <strong>Câmera:</strong> Aponte para o QR code ou código de barras</li>
          <li>• <strong>Upload:</strong> Envie uma foto com o código</li>
          <li>• <strong>Manual:</strong> Digite o SKU ou código de barras</li>
        </ul>
        
        {hasPermission === false && (
          <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded">
            <p className="text-xs text-yellow-700">
              ⚠️ <strong>Permissão necessária:</strong> Para usar a câmera, você precisa permitir o acesso nas configurações do navegador.
            </p>
          </div>
        )}
      </div>

      {/* Ações */}
      <div className="flex justify-end">
        <Button
          type="button"
          variant="outline"
          onClick={() => {
            handleStopCamera()
            onCancel()
          }}
        >
          Fechar
        </Button>
      </div>
    </div>
  )
}

export default QRCodeScanner